import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LockedGate from "@/components/LockedGate";
import EbookHub from "@/components/EbookHub";
import EbookCover from "@/components/EbookCover";
import PdfViewerDialog from "@/components/PdfViewerDialog";
import { trackContent } from "@/lib/track";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookOpen, PlayCircle, Radio, CalendarClock, TrendingUp, TrendingDown, XCircle, CheckCircle2, ExternalLink, Lock, Clock as ClockIcon, Presentation, Loader2, Download } from "lucide-react";
import Whiteboard from "@/components/Whiteboard";
import MiniChart, { normalizeSymbol } from "@/components/MiniChart";
import { normalizeVideoUrl } from "@/lib/videoUrl";

const EBOOK_COVER = "https://images.unsplash.com/photo-1574884280706-7342ca3d4231?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxlYm9vayUyMGNvdmVyJTIwZmluYW5jZXxlbnwwfHx8fDE3ODU1MDM2MDF8MA&ixlib=rb-4.1.0&q=85";
const COACHING_COVER = "https://images.unsplash.com/photo-1579226905180-636b76d96082?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx0cmFkaW5nJTIwY29hY2hpbmclMjBtZW50b3JzaGlwfGVufDB8fHx8MTc4NTUwMzYwMXww&ixlib=rb-4.1.0&q=85";

function SignalCard({ s }) {
  const dirColor = s.direction === "buy" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200";
  const statusMeta = {
    active: { label: "ACTIVE", cls: "bg-[#00b4ff] text-white", Icon: Radio, live: true },
    hit_tp: { label: "HIT TP", cls: "bg-emerald-600 text-white", Icon: CheckCircle2 },
    hit_sl: { label: "HIT SL", cls: "bg-rose-600 text-white", Icon: XCircle },
    cancelled: { label: "CANCELLED", cls: "bg-slate-500 text-white", Icon: XCircle },
  }[s.status] || { label: s.status, cls: "bg-slate-500 text-white", Icon: Radio };

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white" data-testid={`signal-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-display font-bold text-2xl tracking-tight">{s.pair}</div>
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase border ${dirColor}`}>
              {s.direction === "buy" ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {s.direction}
            </div>
          </div>
          {s.note && <div className="mt-1 text-sm text-slate-600 truncate">{s.note}</div>}
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono-plex tracking-widest ${statusMeta.cls}`}>
          {statusMeta.live && <span className="live-dot" />}
          <statusMeta.Icon size={11} /> {statusMeta.label}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100">
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">Entry</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold">{s.entry ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">TP</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold text-emerald-700">{s.tp}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">SL</div>
          <div className="mt-1 font-mono-plex text-sm font-semibold text-rose-700">{s.sl}</div>
        </div>
      </div>
      <div className="mt-4 -mx-1 rounded-lg border border-slate-100 bg-slate-50/60 overflow-hidden">
        <MiniChart symbol={normalizeSymbol(s.pair)} height={130} />
      </div>
      <div className="mt-4 text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">
        {new Date(s.created_at).toLocaleString()}
      </div>
    </div>
  );
}

function SignalsSection() {
  const [signals, setSignals] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/signals"); setSignals(data); } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return signals;
    return signals.filter((s) => s.status === filter);
  }, [signals, filter]);

  const filters = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "hit_tp", label: "Hit TP" },
    { key: "hit_sl", label: "Hit SL" },
    { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`signal-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase border ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">
          Tiada signals lagi untuk filter ini.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((s, i) => (
            <div key={s.id} className="stagger-item" style={{ animationDelay: `${i * 40}ms` }}>
              <SignalCard s={s} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EbooksSection({ onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [requesting, setRequesting] = useState({});
  const [viewer, setViewer] = useState(null);
  const [opening, setOpening] = useState(null);
  const closeViewer = useCallback(() => {
    setViewer((v) => { if (v) URL.revokeObjectURL(v.url); return null; });
  }, []);
  const onProgress = useCallback((id, page, pages) => {
    setItems((prev) => prev.map((e) => (e.id === id ? { ...e, last_page: page, page_count: pages } : e)));
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/ebooks");
      setItems(data);
      onUnreadChange && onUnreadChange(data.filter((e) => !e.is_read && e.has_access).length);
    } catch (err) { logDev(err); }
  };

  useEffect(() => { load(); }, []);

  const openEbook = async (item) => {
    setOpening(item.id);
    try {
      const res = await api.get(`/files/${item.storage_path}`, { responseType: "blob" });
      const isPdf = /\.pdf$/i.test(item.storage_path) || /\.pdf$/i.test(item.original_filename || "");
      const blob = isPdf ? new Blob([res.data], { type: "application/pdf" }) : res.data;
      const ext = item.storage_path.split(".").pop();
      const filename = item.original_filename || `${item.title}.${ext}`;
      const url = URL.createObjectURL(blob);
      if (isPdf) { setViewer({ id: item.id, title: item.title, url, filename, startPage: item.last_page || 1 }); trackContent("ebook", item.id, "open"); }
      else {
        trackContent("ebook", item.id, "download");
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      // Mark as read (fire-and-forget) and update local state so the ribbon disappears.
      if (!item.is_read) {
        api.post(`/ebooks/${item.id}/read`).catch((err) => logDev(err));
        const next = items.map((e) => (e.id === item.id ? { ...e, is_read: true } : e));
        setItems(next);
        onUnreadChange && onUnreadChange(next.filter((e) => !e.is_read && e.has_access).length);
      }
    } catch (e) {
      toast.error("Gagal buka ebook.");
    } finally {
      setOpening(null);
    }
  };

  const requestUnlock = async (item) => {
    setRequesting((p) => ({ ...p, [item.id]: true }));
    try {
      await api.post(`/ebooks/${item.id}/request-unlock`);
      toast.success("Request sent. Tunggu admin approve.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed.");
    } finally {
      setRequesting((p) => ({ ...p, [item.id]: false }));
    }
  };

  if (items.length === 0) {
    return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">Tiada ebook lagi. Admin akan upload soon.</div>;
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((e, i) => {
        const gated = e.access === "locked" && !e.has_access;
        const status = e.request_status;
        return (
          <div
            key={e.id}
            className="stagger-item relative rounded-2xl border border-slate-200 bg-white overflow-hidden group"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={`ebook-${e.id}`}
            data-unread={!e.is_read && !gated ? "true" : "false"}
          >
            {!e.is_read && !gated && (
              <span
                data-testid={`ebook-new-ribbon-${e.id}`}
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-mono-plex font-bold tracking-widest uppercase bg-[#00b4ff] text-white shadow-[0_6px_16px_-4px_#00b4ff]"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> New
              </span>
            )}
            {gated && (
              <span
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-mono-plex font-bold tracking-widest uppercase bg-amber-500 text-white shadow-[0_6px_16px_-4px_rgba(245,158,11,0.5)]"
              >
                <Lock size={9} /> Locked
              </span>
            )}
            <div
              className={`relative h-40 bg-slate-100 overflow-hidden ${gated ? "" : "cursor-pointer"}`}
              data-testid={`ebook-cover-open-${e.id}`}
              role={gated ? undefined : "button"}
              onClick={gated || opening === e.id ? undefined : () => openEbook(e)}
            >
              <EbookCover path={e.cover_path} fallback={EBOOK_COVER} testId={`ebook-cover-${e.id}`}
                          className={`w-full h-full object-cover group-hover:scale-105 ${gated ? "opacity-40 blur-[2px]" : ""}`} />
              {opening === e.id && <div className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="animate-spin text-[#00b4ff]" /></div>}
              {!gated && e.last_page && e.page_count && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-black/20" data-testid={`ebook-progress-${e.id}`}>
                  <div className="h-full bg-[#00b4ff]" style={{ width: `${Math.round((e.last_page / e.page_count) * 100)}%` }} />
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-semibold tracking-tight">{e.title}</div>
                {e.access === "locked" && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono-plex tracking-widest uppercase bg-amber-50 text-amber-700">
                    <Lock size={9} /> LOCKED
                  </span>
                )}
              </div>
              {e.description && <div className="mt-1.5 text-sm text-slate-600 line-clamp-2">{e.description}</div>}
              {gated ? (
                <div className="mt-4 space-y-2">
                  {status === "pending" && (
                    <div data-testid={`ebook-request-pending-${e.id}`} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-mono-plex tracking-widest uppercase">
                      <ClockIcon size={12} /> Waiting admin
                    </div>
                  )}
                  {status === "rejected" && (
                    <>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-mono-plex tracking-widest uppercase">
                        Rejected
                      </div>
                      {e.reject_reason && <div className="text-[11px] text-rose-700">Reason: {e.reject_reason}</div>}
                      <button
                        data-testid={`ebook-request-again-${e.id}`}
                        disabled={requesting[e.id]}
                        onClick={() => requestUnlock(e)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-xs font-semibold"
                        style={{ transition: "background-color 0.2s ease" }}
                      >
                        {requesting[e.id] ? "Sending…" : "Try again"}
                      </button>
                    </>
                  )}
                  {!status && (
                    <button
                      data-testid={`ebook-request-unlock-${e.id}`}
                      disabled={requesting[e.id]}
                      onClick={() => requestUnlock(e)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-xs font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <Lock size={12} /> {requesting[e.id] ? "Sending…" : "Request Unlock"}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  data-testid={`open-ebook-${e.id}`}
                  onClick={() => openEbook(e)}
                  disabled={opening === e.id}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#00b4ff] hover:text-[#0090cc] disabled:opacity-60"
                  style={{ transition: "color 0.2s ease" }}
                >
                  {opening === e.id ? <><Loader2 size={14} className="animate-spin" /> Opening…</>
                    : e.last_page > 1 ? <>Continue · page {e.last_page}{e.page_count ? ` / ${e.page_count}` : ""} <BookOpen size={14} /></>
                    : <>Open ebook <BookOpen size={14} /></>}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {viewer && <PdfViewerDialog doc={viewer} onClose={closeViewer} onProgress={onProgress} />}
    </div>
  );
}

function VideosSection() {
  const [items, setItems] = useState([]);
  const [videoUrls, setVideoUrls] = useState({});
  const [requesting, setRequesting] = useState({});
  const [playing, setPlaying] = useState({});
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/videos"); setItems(data); } catch (err) { logDev(err); }
  };

  useEffect(() => {
    load();
    return () => { Object.values(videoUrls).forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  const loadVideo = async (v) => {
    if (videoUrls[v.id]) return videoUrls[v.id];
    try {
      const res = await api.get(`/files/${v.storage_path}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      setVideoUrls((prev) => ({ ...prev, [v.id]: blobUrl }));
      return blobUrl;
    } catch (_) {
      toast.error("Gagal load video.");
      return null;
    }
  };
  const playVideo = (v) => loadVideo(v);
  const viewed = React.useRef({});
  const onVideoPlay = (v) => {
    if (viewed.current[v.id]) return;
    viewed.current[v.id] = true;
    trackContent("video", v.id, "view");
  };
  const playEmbed = (v) => {
    setPlaying((p) => ({ ...p, [v.id]: true }));
    trackContent("video", v.id, "view");
  };
  const downloadVideo = async (v) => {
    setDownloading(v.id);
    const url = await loadVideo(v);
    setDownloading(null);
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = v.original_filename || `${v.title}.${v.storage_path.split(".").pop()}`; a.click();
    trackContent("video", v.id, "download");
  };

  const requestUnlock = async (v) => {
    setRequesting((p) => ({ ...p, [v.id]: true }));
    try {
      await api.post(`/videos/${v.id}/request-unlock`);
      toast.success("Request sent. Tunggu admin approve.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed.");
    } finally {
      setRequesting((p) => ({ ...p, [v.id]: false }));
    }
  };

  if (items.length === 0) {
    return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">Tiada e-video lagi.</div>;
  }

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {items.map((v, i) => {
        const gated = v.access === "locked" && !v.has_access;
        const status = v.request_status; // null | pending | rejected | approved
        return (
          <div key={v.id} className="stagger-item rounded-2xl border border-slate-200 bg-white overflow-hidden"
               style={{ animationDelay: `${i * 40}ms` }} data-testid={`video-${v.id}`}>
            <div className="aspect-video bg-slate-100 relative">
              {gated ? (
                <div className="w-full h-full grid place-items-center bg-slate-900 text-white p-6 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Lock size={36} className="text-[#00b4ff]" />
                    <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-400">Locked</div>
                    {status === "pending" && (
                      <div data-testid={`video-request-pending-${v.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-100 text-xs font-mono-plex tracking-widest uppercase">
                        <ClockIcon size={12} /> Waiting admin
                      </div>
                    )}
                    {status === "rejected" && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-100 text-[10px] font-mono-plex tracking-widest uppercase">
                          Rejected
                        </div>
                        {v.reject_reason && (
                          <div className="text-[11px] text-slate-300 max-w-[80%] mt-1">Reason: {v.reject_reason}</div>
                        )}
                        <button
                          data-testid={`video-request-again-${v.id}`}
                          disabled={requesting[v.id]}
                          onClick={() => requestUnlock(v)}
                          className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-xs font-semibold"
                          style={{ transition: "background-color 0.2s ease" }}
                        >
                          {requesting[v.id] ? "Sending…" : "Try again"}
                        </button>
                      </>
                    )}
                    {!status && (
                      <button
                        data-testid={`video-request-unlock-${v.id}`}
                        disabled={requesting[v.id]}
                        onClick={() => requestUnlock(v)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-xs font-semibold"
                        style={{ transition: "background-color 0.2s ease" }}
                      >
                        {requesting[v.id] ? "Sending…" : "Request Unlock"}
                      </button>
                    )}
                  </div>
                </div>
              ) : v.url ? (() => {
                const norm = normalizeVideoUrl(v.url);
                const src = norm ? norm.embedUrl : v.url;
                if (!playing[v.id]) {
                  return (
                    <button onClick={() => playEmbed(v)} data-testid={`play-video-${v.id}`}
                            className="w-full h-full grid place-items-center bg-slate-900 text-white group">
                      <div className="flex flex-col items-center gap-2">
                        <PlayCircle size={44} className="group-hover:scale-110" style={{ transition: "transform 0.2s ease" }} />
                        <span className="text-xs font-mono-plex tracking-widest">TONTON VIDEO</span>
                      </div>
                    </button>
                  );
                }
                return (
                  <iframe
                    src={/youtube|vimeo/.test(src) ? `${src}${src.includes("?") ? "&" : "?"}autoplay=1` : src}
                    title={v.title}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                    allowFullScreen
                    data-testid={`video-iframe-${v.id}`}
                  />
                );
              })() : videoUrls[v.id] ? (
                <video src={videoUrls[v.id]} controls className="w-full h-full" onPlay={() => onVideoPlay(v)} data-testid={`video-player-${v.id}`} />
              ) : (
                <button onClick={() => playVideo(v)} data-testid={`load-video-${v.id}`}
                        className="w-full h-full grid place-items-center bg-slate-900 text-white group">
                  <div className="flex flex-col items-center gap-2">
                    <PlayCircle size={44} className="group-hover:scale-110" style={{ transition: "transform 0.2s ease" }} />
                    <span className="text-xs font-mono-plex tracking-widest">LOAD VIDEO</span>
                  </div>
                </button>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-semibold tracking-tight">{v.title}</div>
                {v.access === "locked" && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono-plex tracking-widest uppercase bg-amber-50 text-amber-700">
                    <Lock size={9} /> LOCKED
                  </span>
                )}
              </div>
              {v.description && <div className="mt-1.5 text-sm text-slate-600 line-clamp-2">{v.description}</div>}
              {!gated && !v.url && v.storage_path && (
                <button
                  data-testid={`download-video-${v.id}`}
                  onClick={() => downloadVideo(v)}
                  disabled={downloading === v.id}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#00b4ff] hover:text-[#0090cc] disabled:opacity-60"
                  style={{ transition: "color 0.2s ease" }}
                >
                  {downloading === v.id ? <><Loader2 size={14} className="animate-spin" /> Downloading…</> : <><Download size={14} /> Download video</>}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CoachingSection({ focusSlotId }) {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [redepositSlot, setRedepositSlot] = useState(null);
  const [redepositAmount, setRedepositAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([api.get("/slots"), api.get("/bookings?mine=true")]);
      setSlots(s.data); setBookings(b.data);
    } catch (err) { logDev(err); }
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll to and briefly highlight the requested slot after slots load
  useEffect(() => {
    if (!focusSlotId || slots.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="slot-${focusSlotId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(focusSlotId);
        setTimeout(() => setHighlightId(null), 2500);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [focusSlotId, slots]);

  const myBookingsBySlot = useMemo(() => Object.fromEntries(bookings.map((b) => [b.slot_id, b])), [bookings]);

  const submitBooking = async (slot, amount) => {
    setBusy(true);
    try {
      await api.post("/bookings", { slot_id: slot.id, redeposit_amount: amount });
      toast.success("Booking submitted. Menunggu admin check redeposit dan accept.");
      setRedepositSlot(null); setRedepositAmount("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed.");
    } finally { setBusy(false); }
  };

  const handleBookClick = (slot) => {
    if (slot.requires_redeposit) {
      setRedepositSlot(slot);
      setRedepositAmount("");
    } else {
      submitBooking(slot, null);
    }
  };

  const confirmRedeposit = (e) => {
    e.preventDefault();
    const amt = parseFloat(redepositAmount);
    if (!amt || amt <= 0) { toast.error("Sila masukkan nilai redeposit yang sah."); return; }
    submitBooking(redepositSlot, amt);
  };

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 mb-6">
        <img src={COACHING_COVER} alt="" className="h-40 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-5">
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">1-on-1</div>
          <div className="font-display text-2xl font-bold tracking-tight">Book a coaching seat</div>
        </div>
      </div>

      {slots.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">
          Admin belum publish slot. Balik lagi.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slots.map((s) => {
            const mine = myBookingsBySlot[s.id];
            const full = (s.booked_count || 0) >= (s.capacity || 1);
            const activeBooking = mine && ["pending", "accepted"].includes(mine.status);
            return (
              <div
                key={s.id}
                className={`rounded-2xl border bg-white p-5 flex flex-col ${highlightId === s.id ? "border-[#00b4ff] ring-4 ring-[#00b4ff]/20" : "border-slate-200"}`}
                style={{ transition: "border-color 0.3s ease, box-shadow 0.3s ease" }}
                data-testid={`slot-${s.id}`}
              >
                <div className="flex items-center gap-2 text-[#00b4ff]">
                  <CalendarClock size={16} />
                  <div className="font-mono-plex text-sm tracking-widest">{s.date} · {s.time}</div>
                </div>
                <div className="mt-2 font-display text-lg font-semibold tracking-tight">{s.title}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-slate-100 text-slate-600">
                    {s.booked_count || 0}/{s.capacity} seats
                  </span>
                  {s.requires_redeposit && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase bg-amber-50 text-amber-700">
                      Redeposit required
                    </span>
                  )}
                </div>

                {s.requirements && (
                  <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 mb-1">Syarat untuk join</div>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{s.requirements}</p>
                  </div>
                )}

                <div className="mt-auto pt-4">
                  {mine ? (
                    <div className="w-full inline-flex items-center justify-center gap-2 text-xs font-mono-plex tracking-widest uppercase text-slate-700 bg-slate-100 px-3 py-2.5 rounded-full">
                      {mine.status === "accepted" ? "CONFIRMED ✓" :
                       mine.status === "rejected" ? "REJECTED" :
                       mine.status === "cancelled" ? "CANCELLED" : "PENDING"}
                      {mine.redeposit_amount != null && <span className="normal-case tracking-normal font-semibold text-[#00b4ff]">· ${Number(mine.redeposit_amount).toFixed(2)}</span>}
                    </div>
                  ) : (
                    <button
                      data-testid={`book-slot-${s.id}`}
                      disabled={full || busy}
                      onClick={() => handleBookClick(s)}
                      className="w-full px-4 py-2.5 rounded-full text-sm font-semibold bg-[#00b4ff] hover:bg-[#0090cc] disabled:bg-slate-300 text-white"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      {full ? "Fully booked" : s.requires_redeposit ? "Apply with redeposit" : "Book seat"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Redeposit dialog */}
      {redepositSlot && (
        <div className="fixed inset-0 z-[60] grid grid-cols-[minmax(0,1fr)] place-items-center bg-slate-900/50 backdrop-blur-sm p-5" data-testid="redeposit-dialog">
          <form onSubmit={confirmRedeposit} className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-[#00b4ff]">Redeposit required</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Apply for {redepositSlot.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Slot ini memerlukan redeposit. Sila masukkan nilai redeposit anda (USD). Admin akan check dahulu sebelum accept.
            </p>
            {redepositSlot.requirements && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                {redepositSlot.requirements}
              </div>
            )}
            <div className="mt-5">
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Redeposit amount (USD)</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-slate-500 font-mono-plex">$</span>
                <input
                  data-testid="redeposit-input"
                  autoFocus
                  required
                  type="number"
                  min="1"
                  step="0.01"
                  value={redepositAmount}
                  onChange={(e) => setRedepositAmount(e.target.value)}
                  placeholder="50.00"
                  className="flex-1 min-w-0 w-full px-4 py-3 rounded-lg border border-slate-200 font-mono-plex focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => { setRedepositSlot(null); setRedepositAmount(""); }} className="px-5 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }} data-testid="redeposit-cancel">
                Cancel
              </button>
              <button type="submit" disabled={busy} data-testid="redeposit-submit" className="px-5 py-2.5 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white text-sm font-semibold" style={{ transition: "background-color 0.2s ease" }}>
                {busy ? "Submitting…" : "Submit for review"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default function TradePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "ebooks";
  const focusSlot = params.get("slot");
  const [ebookUnread, setEbookUnread] = useState(0);

  const setTab = (v) => {
    const next = new URLSearchParams(params);
    next.set("tab", v);
    if (v !== "coaching") next.delete("slot");
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-[#00b4ff]">Members area</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight">Education</h1>
        <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
          Ebooks, e-videos dan coaching bookings — semua bahan pembelajaran di sini.
        </p>
      </div>

      <div className="mt-10">
        <LockedGate feature="Education">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-slate-100 p-1 rounded-full max-w-full overflow-x-auto justify-start no-scrollbar">
              <TabsTrigger data-testid="tab-ebooks" value="ebooks" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm relative">
                <BookOpen size={14} className="mr-2" /> Ebook
                {ebookUnread > 0 && (
                  <span
                    data-testid="tab-ebooks-unread-badge"
                    className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#00b4ff] text-white text-[10px] font-mono-plex font-bold grid place-items-center"
                  >
                    {ebookUnread > 9 ? "9+" : ebookUnread}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger data-testid="tab-videos" value="videos" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <PlayCircle size={14} className="mr-2" /> E-Video
              </TabsTrigger>
              <TabsTrigger data-testid="tab-coaching" value="coaching" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <CalendarClock size={14} className="mr-2" /> Coaching
              </TabsTrigger>
              <TabsTrigger data-testid="tab-whiteboard" value="whiteboard" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow font-display font-semibold text-sm">
                <Presentation size={14} className="mr-2" /> Whiteboard
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ebooks" className="mt-6"><EbookHub ebooks={<EbooksSection onUnreadChange={setEbookUnread} />} /></TabsContent>
            <TabsContent value="videos" className="mt-6"><VideosSection /></TabsContent>
            <TabsContent value="coaching" className="mt-6"><CoachingSection focusSlotId={focusSlot} /></TabsContent>
            <TabsContent value="whiteboard" className="mt-6"><Whiteboard /></TabsContent>
          </Tabs>
        </LockedGate>
      </div>
    </div>
  );
}
